import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components/macro';

import {
    Add,
    Delete,
    Edit,
} from '@material-ui/icons';
import {
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    Fade,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    useMediaQuery,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import SimpleModal from '../../shared/SimpleModal';
import solicitationService from '../../../domain/services/solicitation/solicitation.service';
import loginService from '../../../domain/services/login.service';
import requestRegistryService from '../../../domain/services/solicitation/request.registry.service';
import requestPartService from '../../../domain/services/solicitation/request.part.service';
import stepsSolicitation from '../../../domain/constant/steps.solicitation.constant';

const partFadeIn = keyframes`
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
`;

const TableWrapper = styled.div`
    overflow-y: auto;
    max-width: calc(100vw - 96px);

    tr[data-saved] {
        animation: ${partFadeIn} 300ms ease-out;
    }

    tbody tr {
        transition: background-color 150ms ease;
    }

    tbody tr:hover {
        background-color: #f9fafb;
    }
`;

const MobileCardsWrapper = styled.div`
    .part-saved {
        animation: ${partFadeIn} 300ms ease-out;
    }
`;

const textColorByVariant = {
    neutral: '#667085',
    primary: '#1d4ed8',
    danger: '#dc2626',
};

const headerStyles = {
    color: '#667085',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.35,
};

const getEmptyPart = () => ({
    value: '',
    partTypeId: '',
    isRepresented: false,
});

const normalizePart = (part = {}) => ({
    ...part,
    value: (part.value || part.name || '').trim(),
});

const asComparableValue = (value) =>
    value == null ? '' : `${value}`;

const getPartName = (part = {}) =>
    part.value || part.name || '';

const getPartPayload = (part = {}) => ({
    value: part.value,
    partTypeId: part.partTypeId,
    isRepresented: part.isRepresented,
});

const getEditablePayload = (part = {}) => ({
    id: part.id,
    ...getPartPayload(part),
});

const getPartTypeIdsMatch = (partType, currentPartType) =>
    asComparableValue(partType.id) === asComparableValue(currentPartType);

const getSaveErrorMessage = (error) =>
    error.response?.data?.message || 'Um erro ocorreu ao tentar salvar a parte! Contate o suporte para que seu problema seja resolvido!';

const getRemoveErrorMessage = (error) =>
    error.response?.data?.message || 'Um erro ocorreu ao tentar remover a parte! Contate o suporte para que seu problema seja resolvido!';

const MODAL_FIELD_MESSAGES = {
    value: 'Preencha o nome da parte',
    partTypeId: 'Selecione o tipo de parte',
};

function PartsForm({ request = {}, setRequest, isEdit, setAlert, fieldProps = {} }) {

    const parts = useMemo(() => request.parts || [], [request.parts]);
    const isSmallScreen = useMediaQuery('(max-width:959.95px)', { noSsr: true });
    const isDesktopFullWidth = useMediaQuery('(min-width:1280px)', { noSsr: true });
    const useStackedLayout = isSmallScreen || !isDesktopFullWidth;

    const [partTypes, setPartTypes] = useState([]);
    const [editableParts, setEditableParts] = useState(parts);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [removingPartId, setRemovingPartId] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalPart, setModalPart] = useState(null);
    const [modalFieldErrors, setModalFieldErrors] = useState({});

    const requestPartsRef = React.useRef(parts);
    const isEditRef = React.useRef(isEdit);
    const loadingRequestRef = React.useRef(0);

    useEffect(() => {
        setEditableParts(parts);
        requestPartsRef.current = parts;
    }, [parts]);

    useEffect(() => {
        isEditRef.current = isEdit;
    }, [isEdit]);

    useEffect(() => {

        let isActive = true;
        const currentLoadingRequest = loadingRequestRef.current + 1;
        loadingRequestRef.current = currentLoadingRequest;

        if (!request.modalityId) {
            setIsLoading(false);
            setPartTypes([]);
            return undefined;
        }

        const loadPartTypes = async () => {
            setIsLoading(true);

            try {
                const { data = [] } = await solicitationService.getPartTypes(request.modalityId);

                if (!isActive) return;

                const nextPartTypes = data || [];
                const currentParts = requestPartsRef.current || [];
                const validParts = currentParts.filter((part) =>
                    nextPartTypes.some((partType) => getPartTypeIdsMatch(partType, part.partTypeId)));
                const invalidParts = currentParts.filter((part) =>
                    !nextPartTypes.some((partType) => getPartTypeIdsMatch(partType, part.partTypeId)));

                if (!isEditRef.current) {
                    invalidParts
                        .filter((part) => !!part.id)
                        .forEach((part) => requestRegistryService.removePart(part).catch(() => null));

                    setEditableParts(validParts);
                    setRequest((currentRequest) => ({ ...currentRequest, parts: validParts }));
                }

                setPartTypes(nextPartTypes);
            } catch (error) {
                if (!isActive) return;
                setPartTypes([]);
            } finally {
                if (loadingRequestRef.current !== currentLoadingRequest) return;
                setIsLoading(false);
            }
        };

        loadPartTypes();

        return () => { isActive = false; };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request.modalityId]);

    const changeRequestParts = (newParts) =>
        setRequest((currentRequest) => ({ ...currentRequest, parts: newParts }));

    const syncParts = (newParts) => {
        setEditableParts(newParts);
        changeRequestParts(newParts);
    };

    const cantEdit = () =>
        loginService.isFaciliter() || (isEdit && !solicitationService.canAddContent(request));

    const isNotionIndication = () =>
        request.status === stepsSolicitation.NEW && !!request.referenceNotionRequestId;

    const isInteractionDisabled = !!fieldProps.disabled || (cantEdit() && !isNotionIndication());

    const disableStyle = isInteractionDisabled
        ? { pointerEvents: 'none', opacity: 1, cursor: 'not-allowed' }
        : {};

    const canAdd = !cantEdit() || isNotionIndication();

    const hasRepresentedConflict = (part, currentParts = editableParts) => {
        if (!part.isRepresented) return false;
        return currentParts
            .filter((ep) => ep.id !== part.id)
            .filter((ep) => ep.isRepresented)
            .some((ep) => asComparableValue(ep.partTypeId) !== asComparableValue(part.partTypeId));
    };

    // --- Modal ---

    const openCreateModal = () => {
        setModalPart(getEmptyPart());
        setModalFieldErrors({});
        setModalOpen(true);
    };

    const openEditModal = (part) => {
        setModalPart({ ...part, value: getPartName(part) });
        setModalFieldErrors({});
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalPart(null);
        setModalFieldErrors({});
    };

    const updateModalField = (key, value) => {
        setModalPart((current) => ({ ...current, [key]: value }));
        setModalFieldErrors((current) => {
            if (!current[key]) return current;
            const next = { ...current };
            delete next[key];
            return next;
        });
    };

    const validateModal = () => {
        const normalized = normalizePart(modalPart);
        const missing = requestPartService.getMissingFieldsToSavePart(normalized);

        if (missing.length > 0) {
            const errors = {};
            missing.forEach((key) => {
                if (MODAL_FIELD_MESSAGES[key]) errors[key] = MODAL_FIELD_MESSAGES[key];
            });
            setModalFieldErrors(errors);
            return { isValid: false, normalized };
        }

        return { isValid: true, normalized };
    };

    const handleModalSubmit = async () => {
        const { isValid, normalized } = validateModal();
        if (!isValid) return;

        setIsSaving(true);
        try {
            if (modalPart.id) {
                await requestRegistryService.editPart(getEditablePayload(normalized));
                syncParts(editableParts.map((ep) => ep.id === normalized.id ? { ...ep, ...normalized } : ep));
            } else {
                const response = await requestRegistryService.createPart(request.id, getPartPayload(normalized));
                syncParts([...editableParts, response.data]);
            }
            closeModal();
        } catch (error) {
            setAlert(getSaveErrorMessage(error), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // --- Remove ---

    const removePart = async (part) => {
        setRemovingPartId(part.id);
        try {
            await requestRegistryService.removePart(part);
            syncParts(editableParts.filter((ep) => ep.id !== part.id));
            setAlert('Parte removida com sucesso!', 'success');
        } catch (error) {
            setAlert(getRemoveErrorMessage(error), 'error');
        } finally {
            setRemovingPartId(null);
        }
    };

    // --- Render helpers ---

    const getTypePillVariant = (part = {}) => {
        if (!part.partTypeId) return 'neutral';
        return part.isRepresented ? 'primary' : 'danger';
    };

    const getPartTypeName = (partTypeId) => {
        const pt = partTypes.find((t) => getPartTypeIdsMatch(t, partTypeId));
        return pt?.name || '';
    };

    const renderTypeBadge = (part) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: textColorByVariant[getTypePillVariant(part)], whiteSpace: 'nowrap' }}>
            {getPartTypeName(part.partTypeId)}
        </span>
    );

    const renderRepresentedBadge = (part) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: textColorByVariant[part.isRepresented ? 'primary' : 'danger'], whiteSpace: 'nowrap' }}>
            {part.isRepresented ? 'Sim' : 'Não'}
        </span>
    );

    const renderRowActions = (part) => {
        if (!canAdd) return null;
        const isRemoving = removingPartId === part.id;
        return (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <Tooltip title="Editar parte" arrow>
                    <span>
                        <IconButton
                            aria-label={`Editar parte ${part.id}`}
                            onClick={() => openEditModal(part)}
                            disabled={!!fieldProps.disabled || isRemoving}
                            style={{ padding: 6 }}
                        >
                            <Edit style={{ fontSize: 16, color: '#667085' }} />
                        </IconButton>
                    </span>
                </Tooltip>
                {isRemoving ? (
                    <CircularProgress size={16} style={{ margin: 6 }} />
                ) : (
                    <Tooltip title="Excluir parte" arrow>
                        <span>
                            <IconButton
                                aria-label={`Excluir parte ${part.id}`}
                                onClick={() => removePart(part)}
                                disabled={!!fieldProps.disabled}
                                style={{ padding: 6 }}
                            >
                                <Delete style={{ fontSize: 16, color: '#dc2626' }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </div>
        );
    };

    const getPartRow = (part) => (
        <TableRow key={part.id} data-saved="" style={{ borderBottom: '1px solid #edf2f7' }}>
            <TableCell component="th" scope="row" padding="none" style={{ paddingTop: 10, paddingBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 400, color: '#101828', lineHeight: 1.4, wordBreak: 'break-word' }}>
                    {getPartName(part)}
                </span>
            </TableCell>

            <TableCell align="center" padding="none" style={{ width: 80, paddingTop: 10, paddingBottom: 10, verticalAlign: 'middle' }}>
                {renderTypeBadge(part)}
            </TableCell>

            <TableCell align="center" padding="none" style={{ width: 90, paddingTop: 10, paddingBottom: 10, verticalAlign: 'middle' }}>
                {renderRepresentedBadge(part)}
            </TableCell>

            {canAdd && (
                <TableCell padding="none" style={{ width: 56, paddingLeft: 4, verticalAlign: 'middle' }}>
                    {renderRowActions(part)}
                </TableCell>
            )}
        </TableRow>
    );

    const renderMobileCard = (part) => (
        <div key={part.id} className="part-saved" style={{ padding: 14, border: '1px solid #edf2f7', borderRadius: 8, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#101828', lineHeight: 1.4, wordBreak: 'break-word' }}>
                        {getPartName(part)}
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: '#667085' }}>
                        <span>{getPartTypeName(part.partTypeId)}</span>
                        <span style={{ color: '#d0d5dd' }}>·</span>
                        <span style={{ color: textColorByVariant[part.isRepresented ? 'primary' : 'danger'] }}>
                            {part.isRepresented ? 'Representada' : 'Não representada'}
                        </span>
                    </div>
                </div>
                {canAdd && renderRowActions(part)}
            </div>
        </div>
    );

    const showRepresentedWarning = modalOpen && modalPart?.isRepresented && hasRepresentedConflict(normalizePart(modalPart));

    const renderModal = () => (
        <SimpleModal
            open={modalOpen}
            onClose={closeModal}
            title={modalPart?.id ? 'Editar parte' : 'Adicionar parte'}
            maxWidth="sm"
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <Fade in={modalOpen} timeout={400} style={{ transitionDelay: '100ms' }}>
                    <div>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Nome completo da parte"
                            placeholder="Ex: João da Silva"
                            value={modalPart?.value || ''}
                            error={!!modalFieldErrors.value}
                            onChange={(e) => updateModalField('value', e.target.value)}
                            disabled={isSaving}
                            autoFocus
                            inputProps={{ 'aria-label': 'Nome da parte' }}
                        />
                        <Collapse in={!!modalFieldErrors.value} timeout={250}>
                            <div style={{ fontSize: 12, color: '#d32f2f', marginTop: 4, marginLeft: 14 }}>
                                {modalFieldErrors.value}
                            </div>
                        </Collapse>
                    </div>
                </Fade>

                <Fade in={modalOpen} timeout={400} style={{ transitionDelay: '200ms' }}>
                    <div>
                        <TextField
                            fullWidth
                            select
                            variant="outlined"
                            label="Tipo de parte"
                            value={modalPart?.partTypeId || ''}
                            error={!!modalFieldErrors.partTypeId}
                            onChange={(e) => updateModalField('partTypeId', e.target.value)}
                            disabled={isSaving}
                            inputProps={{ 'aria-label': 'Tipo da parte' }}
                        >
                            <MenuItem value="" disabled>Selecione o tipo</MenuItem>
                            {partTypes.map((pt) => (
                                <MenuItem key={pt.id} value={pt.id}>{pt.name}</MenuItem>
                            ))}
                        </TextField>
                        <Collapse in={!!modalFieldErrors.partTypeId} timeout={250}>
                            <div style={{ fontSize: 12, color: '#d32f2f', marginTop: 4, marginLeft: 14 }}>
                                {modalFieldErrors.partTypeId}
                            </div>
                        </Collapse>
                    </div>
                </Fade>

                <Fade in={modalOpen} timeout={400} style={{ transitionDelay: '300ms' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={modalPart?.isRepresented || false}
                                onChange={() => updateModalField('isRepresented', !modalPart?.isRepresented)}
                                color="primary"
                                disabled={isSaving}
                            />
                        }
                        label="É representada"
                    />
                </Fade>

                <Collapse in={showRepresentedWarning} timeout={350}>
                    <Fade in={showRepresentedWarning} timeout={300}>
                        <Alert severity="warning" style={{ fontSize: 12 }}>
                            Você já representa outra parte de tipo diferente neste processo. Tem certeza que deseja representar ambas?
                        </Alert>
                    </Fade>
                </Collapse>
            </div>

            <Fade in={modalOpen} timeout={400} style={{ transitionDelay: '400ms' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                    <Button variant="outlined" onClick={closeModal} disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleModalSubmit} disabled={isSaving}>
                        {modalPart?.id ? 'Salvar' : 'Adicionar parte'}
                        {isSaving && <CircularProgress size={16} style={{ marginLeft: 8, color: '#fff' }} />}
                    </Button>
                </div>
            </Fade>
        </SimpleModal>
    );

    return (
        <Grid container direction="column" style={disableStyle} id="parts">

            {isLoading && (
                <div style={{ marginTop: 15, width: '100%' }}>
                    <CircularProgress size={24} />
                </div>
            )}

            {!isLoading && !partTypes.length && (
                <Alert severity="info" style={{ marginTop: 15 }}>
                    Para informar as partes, selecione a modalidade na aba de informações.
                </Alert>
            )}

            {!isLoading && !!partTypes.length && (
                <Grid container direction="column" spacing={2} style={{ padding: '8px 0' }}>

                    <Grid item container justifyContent="space-between" alignItems="center" style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: '#101828' }}>
                            Partes
                        </div>
                        {canAdd && (
                            <Button
                                variant="outlined" color="primary" size="small"
                                onClick={openCreateModal}
                                disabled={!!fieldProps.disabled}
                            >
                                <Add style={{ marginRight: 5, fontSize: 15 }} />
                                Adicionar parte
                            </Button>
                        )}
                    </Grid>

                    {editableParts.length === 0 && (
                        <Grid item>
                            <div style={{ textAlign: 'center', padding: '20px 0', color: '#98a2b3', fontSize: 13 }}>
                                Nenhuma parte adicionada. Clique em "Adicionar parte" para começar.
                            </div>
                        </Grid>
                    )}

                    {editableParts.length > 0 && (
                        <Grid item style={{ width: '100%', marginTop: 4 }}>
                            {useStackedLayout ? (
                                <MobileCardsWrapper>
                                    {editableParts.map((part) => renderMobileCard(part))}
                                </MobileCardsWrapper>
                            ) : (
                                <TableContainer style={{ width: '100%' }}>
                                    <TableWrapper>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell padding="none" style={headerStyles}>Nome</TableCell>
                                                    <TableCell align="center" padding="none" style={{ ...headerStyles, width: 80, paddingBottom: 4 }}>Tipo de Parte</TableCell>
                                                    <TableCell align="center" padding="none" style={{ ...headerStyles, width: 90, paddingBottom: 4, whiteSpace: 'nowrap' }}>É representada</TableCell>
                                                    {canAdd && <TableCell padding="none" style={{ ...headerStyles, width: 56 }}></TableCell>}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {editableParts.map((part) => getPartRow(part))}
                                            </TableBody>
                                        </Table>
                                    </TableWrapper>
                                </TableContainer>
                            )}
                        </Grid>
                    )}

                </Grid>
            )}

            {renderModal()}
        </Grid>
    );
}

export default PartsForm;
